import { createFileRoute, useRouter } from "@tanstack/react-router";
import React, { Suspense, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/base/table";
import { GetPendingRoutesResponseItem } from "src/api/generated/frontend_internal_v1";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/20/solid";
import { ApiInternal } from "src/api/api_internal";
import TablePagination from "src/components/table-pagination";
import WS from "src/api/ws";
import { Button, PrimaryButton } from "src/components/base/button";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import { useForm } from "@tanstack/react-form";
import Form from "src/components/base/form";
import { Field, FieldGroup, Fieldset, Label } from "src/components/base/fieldset";
import { Textarea } from "src/components/base/textarea";
import { TagBadge } from "src/components/base/badges/tag";

/**
 * The properties for {@link PendingSubnetsOverview}
 */
export type PendingSubnetsOverviewProps = {};

const LIMIT = 25;

/**
 * Overview for all subnets
 */
function PendingSubnetsOverview(props: PendingSubnetsOverviewProps) {
    const [t] = useTranslation("admin-subnets");
    const [tg] = useTranslation();

    const [openApprove, setOpenApprove] = React.useState<GetPendingRoutesResponseItem>();
    const [openReject, setOpenReject] = React.useState<GetPendingRoutesResponseItem>();

    const { page } = Route.useSearch();
    const router = useRouter();

    const form = useForm({
        defaultValues: {
            description: "",
        },
        onSubmit: async ({ value }) => {
            if (!openApprove) return;
            await ApiInternal.subnets.approvePendingRoute(openApprove.uuid, {
                description: value.description,
            });

            setOpenApprove(undefined);
            await router.invalidate({ sync: true });
        },
    });

    const data = Route.useLoaderData();

    useEffect(() => {
        const listener = WS.addEventListener("message.SubnetsChanged", () => {
            router.invalidate({ sync: true });
        });

        return () => WS.removeEventListener(listener);
    }, []);

    return (
        <div className={"flex flex-col gap-6"}>
            <Table dense={true}>
                <TableHead>
                    <TableRow>
                        <TableHeader>{t("label.prefix")}</TableHeader>
                        <TableHeader>{t("label.device-router")}</TableHeader>
                        <TableHeader className={"w-0"}>
                            <span className={"sr-only"}>{tg("accessibility.actions")}</span>
                        </TableHeader>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {data.items.map((subnet) => (
                        <TableRow key={subnet.uuid}>
                            <TableCell>{subnet.subnet.name}</TableCell>
                            <TableCell>
                                <div className={"flex flex-col gap-1"}>
                                    <span>{subnet.device.dnsname}</span>
                                    <div className={"flex gap-2"}>
                                        {subnet.device.tags.map((tag) => (
                                            <TagBadge key={tag.uuid} tag={tag} />
                                        ))}
                                    </div>
                                </div>
                            </TableCell>
                            <TableCell>
                                <div className={"flex gap-1"}>
                                    <Button plain={true} onClick={() => setOpenApprove(subnet)}>
                                        <CheckCircleIcon />
                                    </Button>
                                    <Button plain={true} onClick={() => setOpenReject(subnet)}>
                                        <XCircleIcon />
                                    </Button>
                                </div>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <TablePagination
                currentPage={page}
                maxPages={data.total == 0 ? 1 : Math.ceil(data.total / LIMIT)}
                href={"/a/subnets/pending"}
                getSearchParams={(newPage: number) => ({ page: newPage })}
            />
            {openApprove && (
                <Suspense>
                    <Dialog open={true} onClose={() => setOpenApprove(undefined)}>
                        <DialogTitle>{t("heading.approve-subnet")}</DialogTitle>
                        <DialogBody>
                            <Form onSubmit={form.handleSubmit}>
                                <Fieldset>
                                    <FieldGroup>
                                        <form.Field name={"description"}>
                                            {(fieldApi) => (
                                                <Field>
                                                    <Label>{t("label.description")}</Label>
                                                    <Textarea
                                                        rows={4}
                                                        maxLength={1024}
                                                        onChange={(e) => fieldApi.handleChange(e.target.value)}
                                                        value={fieldApi.state.value}
                                                    />
                                                </Field>
                                            )}
                                        </form.Field>
                                    </FieldGroup>
                                </Fieldset>

                                <DialogActions>
                                    <Button plain={true} onClick={() => setOpenApprove(undefined)}>
                                        {tg("button.cancel")}
                                    </Button>
                                    <PrimaryButton type={"submit"}>{t("button.approve")}</PrimaryButton>
                                </DialogActions>
                            </Form>
                        </DialogBody>
                    </Dialog>
                </Suspense>
            )}

            {openReject && (
                <Suspense>
                    <Dialog open={true} onClose={() => setOpenReject(undefined)}>
                        <DialogTitle>{t("heading.reject-subnet")}</DialogTitle>

                        <DialogActions>
                            <Button plain={true} onClick={() => setOpenReject(undefined)}>
                                {tg("button.cancel")}
                            </Button>
                            <Button
                                color={"red"}
                                onClick={async () => {
                                    openReject.uuid && (await ApiInternal.subnets.rejectPendingRoute(openReject.uuid));
                                    setOpenReject(undefined);
                                    await router.invalidate({ sync: true });
                                }}
                            >
                                {t("button.reject")}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Suspense>
            )}
        </div>
    );
}

/**
 * Type that defines the search parameter of this endpoint
 */
type PendingSubnetsOverviewSearch = {
    /** The current page number we're on */
    page: number;
};

export const Route = createFileRoute("/_admin/a/subnets/_subnets/pending")({
    component: PendingSubnetsOverview,
    validateSearch: (search: Record<string, unknown>): PendingSubnetsOverviewSearch => {
        const page = Number(search?.page ?? 1);

        return { page: page <= 0 ? 1 : page };
    },
    loaderDeps: ({ search: { page } }) => ({ page }),
    loader: async ({ deps }) =>
        await ApiInternal.subnets.getPendingRoutes({
            limit: LIMIT,
            offset: (deps.page - 1) * LIMIT,
        }),
});
